export const SEASON_SURFACE_STEPS = ['0', '50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;

export interface Rgb {
	r: number;
	g: number;
	b: number;
}

export interface SeasonalPalette {
	pageBg: string;
	shellBg: string;
	sidebarBg: string;
	brandSoft: string;
	tint: string;
	focusRing: string;
}

const WHITE: Rgb = {r: 255, g: 255, b: 255};
const INK: Rgb = {r: 26, g: 15, b: 15};

export function parseHexColor(input: string): Rgb | null {
	const match = /^#([0-9a-f]{6})$/i.exec((input || '').trim());
	if (!match) {
		return null;
	}
	const value = parseInt(match[1], 16);
	return {r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255};
}

export function rgbToHex(color: Rgb): string {
	const channel = (value: number) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
	return `#${channel(color.r)}${channel(color.g)}${channel(color.b)}`.toUpperCase();
}

export function mixRgb(base: Rgb, target: Rgb, weight: number): Rgb {
	const ratio = Math.max(0, Math.min(1, weight));
	return {
		r: base.r + (target.r - base.r) * ratio,
		g: base.g + (target.g - base.g) * ratio,
		b: base.b + (target.b - base.b) * ratio,
	};
}

export function relativeLuminance(color: Rgb): number {
	const channel = (value: number) => {
		const scaled = value / 255;
		return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

/** Remonte vers le blanc, ou descend vers l'encre, jusqu'à une luminance lisible. */
function holdLuminance(color: Rgb, target: Rgb, floor: number | null, ceiling: number | null): Rgb {
	let current = color;
	for (let step = 0; step < 8; step++) {
		const luminance = relativeLuminance(current);
		const tooDark = floor !== null && luminance < floor;
		const tooLight = ceiling !== null && luminance > ceiling;
		if (!tooDark && !tooLight) {
			return current;
		}
		current = mixRgb(current, target, 0.38);
	}
	return current;
}

/** Encre du loader : sombre sur un fond clair, blanche sur un fond sombre. */
export function loaderInk(background: string): string {
	const color = parseHexColor(background);
	if (!color || relativeLuminance(color) > 0.55) {
		return '#1A0F0F';
	}
	return '#FFFFFF';
}

/**
 * N'applique que le fond de page, le bandeau et la teinte du mode en cours.
 * Les surfaces Prime (champs, cartes, texte) restent celles du thème clair ou sombre.
 */
export function buildSeasonalPalette(
	backgroundLight: string,
	tintLight: string,
	backgroundDark: string,
	tintDark: string,
	dark: boolean,
): SeasonalPalette | null {
	const background = parseHexColor(dark ? backgroundDark : backgroundLight);
	const tint = parseHexColor(dark ? tintDark : tintLight);
	if (!background || !tint) {
		return null;
	}

	const tintOut = dark
		? holdLuminance(tint, WHITE, 0.4, null)
		: holdLuminance(tint, INK, null, 0.9);
	const page = dark
		? holdLuminance(mixRgb(background, tintOut, 0.34), INK, null, 0.18)
		: holdLuminance(mixRgb(background, tintOut, 0.42), WHITE, 0.62, null);
	const shell = dark
		? holdLuminance(mixRgb(page, tintOut, 0.2), INK, null, 0.16)
		: holdLuminance(mixRgb(page, tintOut, 0.16), WHITE, 0.64, null);
	const sidebar = dark
		? holdLuminance(mixRgb(page, tintOut, 0.12), INK, null, 0.2)
		: holdLuminance(mixRgb(page, tintOut, 0.14), WHITE, 0.58, null);
	const soft = dark
		? holdLuminance(mixRgb(page, tintOut, 0.45), INK, null, 0.2)
		: holdLuminance(mixRgb(page, tintOut, 0.28), WHITE, 0.55, null);

	return {
		pageBg: rgbToHex(page),
		shellBg: rgbToHex(shell),
		sidebarBg: rgbToHex(sidebar),
		brandSoft: rgbToHex(soft),
		tint: rgbToHex(tintOut),
		focusRing: `0 0 0 3px color-mix(in srgb, ${rgbToHex(tintOut)}, transparent 45%)`,
	};
}
